import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Col, DatePicker, Dropdown, Input, Row, Tooltip } from "antd";
import {
  MoreHorizontal,
  Plus,
  Menu,
  History,
  Search,
  ArrowRightLeft,
  X,
  Printer,
  Tag,
  Workflow,
  Upload,
} from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import "../../assets/css/dynemic-page.css";
import {
  getExecutiveEmployeeList,
  selectedRow,
  transferLeads,
} from "../../actionCreator/leadListingActionCreater";
import FeatureComingSoon from "../../common/FeatureComingSoon";
import { useAlert } from "../../context/AlertProvider";
import LeadNavigation from "./LeadNavigation";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import useUpgradeAlert from "../../common/hooks/useUpgradeAlert";
import { subscriptionPlans, userRoles } from "../../config";
import {
  getLeadCount,
  getUsageDetails,
} from "../../actionCreator/commonActionCreator";
import { getToken } from "../../utils/auth";
import { getUserInfo } from "../../utils/utils";
import { useDebounce } from "../../common/hooks/useDebounce";
import { ReactComponent as KanbanIcon } from "../../assets/svg/icons/kanban-view.svg";
import { ReactComponent as ExcelIcon } from "../../assets/svg/file-excel-solid.svg";
import { ReactComponent as PdfIcon } from "../../assets/svg/file-pdf-solid.svg";
import TransferLeadsDrawer from "./TransferLeadsDrawer";
import EmailForm from "../../common/EmailForm";
import { ReactComponent as DeleteIcon } from "../../assets/images/webToLead/delete.svg";

const { RangePicker } = DatePicker;

const LeadHeaderFilter = ({
  tagPage,
  downloadExcel,
  downloadPDF,
  printPage,
  setDelete,
  setEdit,
  selectedRecords,
  handleMenuClick,
  companyFilterList,
}) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { subscription, user, isKanbanView } = useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("searchKeyword") || "",
  );
  const [isTransfer, setIsTransfer] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const showUpgradeAlert = useUpgradeAlert();
  const usageLeadCount = useSelector(
    (state) => state?.CommonReducer?.usageleadcount,
  );
  const usageaccountInfo = useSelector(
    (state) => state?.CommonReducer?.usageaccountInfo,
  );
  useEffect(() => {
    const { id, account } = getUserInfo();

    dispatch(getUsageDetails(getToken(), account?.id));
    dispatch(getLeadCount(getToken(), id));
  }, []);

  const onFilterChange = (key, val) => {
    if (val !== undefined) {
      searchParams.set(key, val);
    } else {
      searchParams.delete(key);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  dayjs.extend(utc);

  // 👇 Date range change handler
  const onDateRangeChange = (dates, dateStrings) => {
    if (dateStrings[0] !== "") {
      const startDate = dayjs(dateStrings[0], "YYYY-MM-DD")
        .startOf("day")
        .utc()
        .format();
      const endDate = dayjs(dateStrings[1], "YYYY-MM-DD")
        .endOf("day")
        .utc()
        .format();

      searchParams.set("startDate", startDate);
      searchParams.set("endDate", endDate);
    } else {
      searchParams.delete("startDate");
      searchParams.delete("endDate");
    }

    searchParams.set("page", 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const predefinedRanges = {
    Today: [dayjs(), dayjs()],
    Yesterday: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
    "Last 7 Days": [dayjs().subtract(6, "day"), dayjs()],
    "This Month": [dayjs().startOf("month"), dayjs().endOf("month")],
    "This Year": [dayjs().startOf("year"), dayjs().endOf("year")],
  };

  const startParam = searchParams.get("startDate");
  const endParam = searchParams.get("endDate");

  const defaultValue =
    startParam && endParam
      ? [dayjs(startParam).startOf("day"), dayjs(endParam).endOf("day")]
      : null;

  const items = [
    {
      key: "1",
      label: (
        <div
          className="tw-text-sm tw-flex tw-items-center tw-gap-2.5 tw-text-slate-700 tw-py-1 tw-px-1.5 tw-font-semibold tw-font-inter"
          onClick={downloadExcel}
        >
          <ExcelIcon style={{ width: 16, height: 16, fill: "#217346" }} />
          Export into Excel
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div
          className="tw-text-sm tw-flex tw-items-center tw-gap-2.5 tw-text-slate-700 tw-py-1 tw-px-1.5 tw-font-semibold tw-font-inter"
          onClick={downloadPDF}
        >
          <PdfIcon style={{ width: 16, height: 16, fill: "#e53935" }} />
          Export into PDF
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <div
          className="tw-text-sm tw-flex tw-items-center tw-gap-2.5 tw-text-slate-700 tw-py-1 tw-px-1.5 tw-font-semibold tw-font-inter"
          onClick={printPage}
        >
          <Printer size={16} className="tw-text-slate-500" />
          Print this list
        </div>
      ),
    },
    {
      key: "4",
      label: (
        <div
          className="tw-text-sm tw-flex tw-items-center tw-gap-2.5 tw-text-slate-700 tw-py-1 tw-px-1.5 tw-font-semibold tw-font-inter"
          onClick={() => {
            if (selectedRecords?.length) {
              setDelete(true);
            } else {
              showAlert("warning", "please select record.");
            }
          }}
        >
          <DeleteIcon className="tw-text-red-500" style={{ width: 16, height: 16 }} />
          Mark as delete
        </div>
      ),
    },
  ].filter(Boolean);

  const currentPage = parseInt(searchParams.get("page")) || 1;
  const itemsPerPage = parseInt(searchParams.get("size")) || 10;
  const totalItems = companyFilterList?.totalExecutiveCount;
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  useEffect(() => {
    if (debouncedSearchTerm !== null) {
      onFilterChange(
        "searchKeyword",
        debouncedSearchTerm.trim() !== "" ? debouncedSearchTerm : undefined,
      );
      onFilterChange("page", currentPage || 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  // Map selected leads into bulk recipients for EmailForm
  const bulkRecipients = (selectedRecords || [])
    .map((item) => {
      const email =
        item?.email ||
        item?.executiveData?.emailId ||
        item?.executiveData?.email ||
        (typeof item?.name === "object"
          ? item?.name?.emailId || item?.name?.email
          : "") ||
        "";
      if (!email || typeof email !== "string" || !email.includes("@")) {
        return null;
      }
      const rawFirst =
        item?.executiveData?.firstName ||
        item?.name?.firstname ||
        item?.name?.firstName ||
        "";
      const rawLast =
        item?.executiveData?.lastName ||
        item?.name?.lastname ||
        item?.name?.lastName ||
        "";
      const leadName =
        [rawFirst, rawLast].filter(Boolean).join(" ") ||
        item?.executiveData?.fullName ||
        item?.name?.fullname ||
        (typeof item?.name === "string" ? item.name : "") ||
        email;

      return {
        id: item?.key || email,
        name: leadName,
        email: email.trim(),
        companyName:
          item?.companyName || item?.executiveData?.companyName || "",
      };
    })
    .filter(Boolean);

  return (
    <>
      <Row id="wrapper" className="tw-items-center tw-justify-between tw-py-1">
        <Col
          md={selectedRecords?.length ? 6 : 4}
          xl={selectedRecords?.length ? 5 : 6}
          xxl={selectedRecords?.length ? 4 : 6}
          className="tw-w-full tw-flex tw-items-center tw-gap-2"
        >
          <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-p-0.5 tw-bg-slate-100 tw-w-fit tw-h-[38px] tw-flex tw-items-center tw-justify-center">
            {isKanbanView && (
              <Tooltip title="Kanban View">
                <div
                  className={`tw-rounded-md tw-cursor-pointer tw-size-[32px] tw-flex tw-items-center tw-justify-center tw-transition-colors ${location.search.includes("layout=2") ? "tw-bg-white tw-shadow-sm tw-text-[#5D44FF]" : "tw-text-slate-500 hover:tw-text-slate-700"}`}
                  onClick={() => {
                    if (
                      [
                        subscriptionPlans?.trail,
                        subscriptionPlans?.starter,
                      ].includes(subscription?.subscriptionPackage?.name)
                    ) {
                      showUpgradeAlert();
                    } else {
                      dispatch(selectedRow([]));
                      searchParams.set("layout", 2);
                      navigate(
                        `${location.pathname}?${searchParams.toString()}`,
                      );
                    }
                  }}
                >
                  <KanbanIcon />
                </div>
              </Tooltip>
            )}
            <Tooltip title="List View">
              <div
                className={`tw-rounded-md tw-cursor-pointer tw-size-[32px] tw-flex tw-items-center tw-justify-center tw-transition-colors ${!location.search.includes("layout=2") ? "tw-bg-white tw-shadow-sm tw-text-[#5D44FF]" : "tw-text-slate-500 hover:tw-text-slate-700"}`}
                onClick={() => {
                  dispatch(selectedRow([]));
                  searchParams.set("layout", 1);
                  navigate(`${location.pathname}?${searchParams.toString()}`);
                }}
              >
                <Menu size={18} className="tw-text-slate-600" />
              </div>
            </Tooltip>
          </div>
          {!selectedRecords?.length && (
            <LeadNavigation onFilterChange={onFilterChange} />
          )}
        </Col>
        <Col
          md={selectedRecords?.length ? 18 : 20}
          xl={selectedRecords?.length ? 19 : 18}
          xxl={selectedRecords?.length ? 20 : 18}
          className="tw-p-0"
        >
          <div
            className={`tw-flex tw-items-center ${!selectedRecords?.length ? "tw-justify-end" : "tw-justify-between"} tw-gap-3 tw-w-full`}
          >
            <div>
              {!!selectedRecords?.length && (
                <Button
                  className="!tw-bg-[#EEF0FF] !tw-border-0 !tw-text-[#5D44FF] !tw-h-[38px] !tw-rounded-lg tw-font-bold tw-text-xs hover:!tw-bg-[#e5dfff] tw-transition-all tw-duration-150"
                  onClick={() => {
                    dispatch(selectedRow([]));
                  }}
                >
                  <X size={14} className="tw-mr-1.5 tw-inline tw-text-[#5D44FF]" />
                  {selectedRecords?.length} Lead
                  {selectedRecords?.length > 1 ? "s" : ""} Selected
                </Button>
              )}
            </div>
            <div
              className={`tw-flex tw-items-center tw-justify-end tw-gap-3 tw-w-full`}
            >
              {!!selectedRecords?.length && (
                <>
                  {bulkRecipients.length > 0 ? (
                    <EmailForm recipients={bulkRecipients}>
                      <Button
                        className="!tw-bg-[#5D44FF] hover:!tw-bg-[#4c36e0] !tw-border-0 !tw-text-white !tw-h-[38px] !tw-rounded-lg tw-font-bold tw-text-xs tw-shadow-[0_4px_12px_rgba(93,68,255,0.2)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.3)] tw-flex tw-items-center tw-transition-all tw-duration-150"
                      >
                        <ArrowRightLeft
                          size={16}
                          className="tw-mr-1.5 tw-inline tw-text-white"
                        />
                        <span>Bulk Email</span>
                      </Button>
                    </EmailForm>
                  ) : (
                    <Button
                      className="!tw-bg-[#5D44FF] hover:!tw-bg-[#4c36e0] !tw-border-0 !tw-text-white !tw-h-[38px] !tw-rounded-lg tw-font-bold tw-text-xs tw-shadow-[0_4px_12px_rgba(93,68,255,0.2)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.3)] tw-flex tw-items-center tw-transition-all tw-duration-150"
                      onClick={() => {
                        showAlert(
                          "warning",
                          "No Valid Emails",
                          "None of the selected leads have an email address.",
                        );
                      }}
                    >
                      <ArrowRightLeft
                        size={16}
                        className="tw-mr-1.5 tw-inline tw-text-white"
                      />
                      <span>Bulk Email</span>
                    </Button>
                  )}

                  <Button
                    className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-3.5 !tw-text-xs !tw-font-bold !tw-text-slate-700 hover:!tw-border-slate-300 hover:!tw-bg-slate-50 tw-flex tw-items-center tw-gap-1.5 !tw-border tw-border-solid tw-transition-all tw-duration-150"
                    onClick={() => {
                      setIsTransfer(true);
                    }}
                  >
                    <ArrowRightLeft
                      size={16}
                      className="tw-mr-1.5 tw-inline tw-text-slate-600"
                    />
                    <span className="d-none d-lg-block">Transfer Leads</span>
                  </Button>
                </>
              )}
              {!selectedRecords?.length && (
                <>
                  <Button
                    className="theme-btn !tw-h-[38px] !tw-rounded-lg tw-font-bold tw-text-xs tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.25)] hover:tw-scale-[1.01] tw-transition-all tw-duration-200"
                    onClick={() => {
                      if (
                        usageLeadCount >=
                        usageaccountInfo[0]?.subscriptions?.subscriptionPackage
                          ?.leadCreationLimit
                      ) {
                        showUpgradeAlert();
                      } else {
                        handleMenuClick();
                      }
                    }}
                  >
                    <Plus size={16} className="tw-mr-1.5 tw-inline" />
                    <span className="tw-hidden xl:tw-block">Add Lead</span>
                  </Button>
                  <Button
                    className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-3.5 !tw-text-xs !tw-font-bold !tw-text-slate-700 hover:!tw-border-slate-300 hover:!tw-bg-slate-50 tw-flex tw-items-center tw-gap-1.5 !tw-border tw-border-solid tw-transition-all tw-duration-150"
                    onClick={() => {
                      if (
                        [subscriptionPlans?.trail].includes(
                          subscription?.subscriptionPackage?.name,
                        )
                      ) {
                        showUpgradeAlert();
                      } else {
                        navigate("/lead-import");
                      }
                    }}
                  >
                    <Upload size={15} className="tw-text-slate-600" />
                    <span className="tw-hidden xl:tw-block tw-whitespace-nowrap">
                      Lead Import
                    </span>
                  </Button>

                  <Input
                    value={searchTerm}
                    placeholder="Search"
                    allowClear
                    suffix={<Search size={16} className="tw-text-slate-400" />}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="!tw-h-[38px] tw-w-[200px] !tw-rounded-lg !tw-border-slate-200"
                  />
                  <RangePicker
                    value={defaultValue}
                    ranges={predefinedRanges}
                    hoverPreview={false}
                    needConfirm={true}
                    onChange={onDateRangeChange}
                    className="
                          !tw-h-[38px]
                          tw-w-[200px]
                          !tw-rounded-lg
                          !tw-border-slate-200
                          [&_.ant-picker-input>input]:!tw-h-[36px]
                          [&_.ant-picker-input>input]:!tw-text-xs
                      "
                  />
                </>
              )}
              <Tooltip title="Add Tags">
                <div
                  className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-size-[38px] tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 hover:tw-bg-slate-50 tw-transition-all tw-duration-150"
                  onClick={() => {
                    tagPage();
                  }}
                >
                  <Tag size={18} className="tw-text-slate-600" />
                </div>
              </Tooltip>
              <Tooltip title="Mass Update">
                <div
                  className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-size-[38px] tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 hover:tw-bg-slate-50 tw-transition-all tw-duration-150"
                  onClick={() => {
                    if (selectedRecords?.length) {
                      setEdit(true);
                    } else {
                      showAlert("warning", "please select record.");
                    }
                  }}
                >
                  <History size={18} className="tw-text-slate-600" />
                </div>
              </Tooltip>
              <FeatureComingSoon>
                <Tooltip title="Create Sequence">
                  <div className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-size-[38px] tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 hover:tw-bg-slate-50 tw-transition-all tw-duration-150">
                    <Workflow size={18} className="tw-text-slate-600" />
                  </div>
                </Tooltip>
              </FeatureComingSoon>
              <Dropdown menu={{ items }} trigger={["click"]}>
                <div className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-size-[38px] tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 hover:tw-bg-slate-50 tw-transition-all tw-duration-150">
                  <MoreHorizontal size={20} className="tw-text-slate-600" />
                </div>
              </Dropdown>
            </div>
          </div>
        </Col>
      </Row>
      <TransferLeadsDrawer
        open={isTransfer}
        onClose={() => setIsTransfer(false)}
        onSubmit={async (data) => {
          const leads = selectedRecords?.filter(
            (d) => !!d?.executiveData?.allowedActions?.includes("TRANSFER"),
          );
          const res = await dispatch(
            transferLeads({
              ...data,
              leadIds: leads?.map((d) => d?.key),
            }),
          );

          if (res?.status === 200) {
            showAlert(
              "success",
              `${leads?.length} Lead${leads?.length > 1 ? "s" : ""} Transfer Successfully`,
              <span>
                {leads?.length} Lead{leads?.length > 1 ? "s" : ""} transferred
                to <strong>{data?.newOwnerName}</strong>
              </span>,
            );
            const allParams = {};
            for (const [key, value] of searchParams.entries()) {
              allParams[key] = value;
            }

            dispatch(
              getExecutiveEmployeeList({
                ...allParams,
                page: allParams?.page ? allParams?.page - 1 : 0,
              }),
            );
            dispatch(selectedRow([]));
          }
        }}
        user={user}
        selectedRecords={selectedRecords}
        isAdmin={user?.role === userRoles?.admin}
        selectedCount={selectedRecords?.length}
        currentOwner={user?.firstName + " " + user?.lastName}
      />
    </>
  );
};

export default LeadHeaderFilter;
